import { ConstrainMode, DetailsListLayoutMode, DetailsRow, IColumn, IDetailsListProps } from '@fluentui/react';
import AppContext from 'App/AppContext';
import { IPLink, IPSearchBox, IPShimmeredDetailsList, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPSelection, IPSelectionMode } from 'baseComponents/Utils/Selection';
import AddEditRemoveButtons from 'components/Buttons/AddEditRemoveButtons';
import DialogConfirmDelete from 'components/Dialogs/DialogConfirmDelete';
import TextInfo from 'components/Notification/Info';
import { KeyValueTag } from 'components/Tags/KeyValueTag';
import TagCreator from 'components/Tags/TagCreator';
import TagEditor from 'components/Tags/TagEditor';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { globalFilterDelay } from 'globalConstants';
import { onRenderDetailsHeaderGlobal } from 'globalFunctions';
import { globalStackStylesPaddingScene, globalStackTokensGapMedium } from 'globalStyles';
import Tag from 'models/tag';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiCreateTag, apiDeleteTag, apiGetTags, apiUpdateTag } from 'services/Api/tagService';
import { apiRequest } from 'services/Auth/authConfig';
import AppError from 'utils/appError';
import { ControllerExceptionCodes } from 'utils/controllerException';
import { sortOnTag } from 'utils/sorting';
import TagFormPanel from './TagFormPanel';
import { TagUsageModal } from './TagUsageModal';

// Pure, so it lives at module scope rather than being rebuilt on every render.
const copyAndSort = (lists: Tag[], key: string, descending?: boolean): Tag[] => {
  return lists.sort((a: Tag, b: Tag) => {
    let value = 0;

    if (key === 'tag') {
      value = sortOnTag(a, b);
    } else if (key === 'info') {
      if (!a.tagInfo && !b.tagInfo) return 0;
      if (!a.tagInfo && b.tagInfo) return -1;
      if (a.tagInfo && !b.tagInfo) return 1;

      value = a.tagInfo!.localeCompare(b.tagInfo!);
    } else if (key === 'used') {
      value = a.usedCount - b.usedCount;
    } else if (key === 'systemtype') {
      value = (a.tagSystemType ?? 0) - (b.tagSystemType ?? 0);
    }

    return descending ? -value : value;
  });
};

interface ITabTagsProps {}

const TabTags = (props: ITabTagsProps) => {
  const { t } = useTranslation(['translation', 'tag', 'tags', 'exceptions']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [tags, setTags] = useState<Tag[]>([]);
  const [sortKey, setSortKey] = useState<string>('tag');
  const [sortDesc, setSortDesc] = useState<boolean>(false);
  const [selectedTag, setSelectedTag] = useState<Tag | undefined>(undefined);
  const [addTag, setAddTag] = useState<boolean>(false);
  const [editTag, setEditTag] = useState<boolean>(false);
  const [removeTag, setRemoveTag] = useState<boolean>(false);
  const [tagToShowUsage, setTagToShowUsage] = useState<Tag | undefined>(undefined);
  const appContext = useContext(AppContext);
  const searchTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState<string>();

  const loadData = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);
      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const tags = await apiGetTags(true, accessToken);
      setTags(tags);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();

    //clean-up
    return () => {
      if (searchTimer.current) {
        clearTimeout(searchTimer.current);
      }
    }; // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tagsDisplay = useMemo(() => {
    const base = searchQuery
      ? tags.filter(
          (entry) =>
            entry.value().toLowerCase().includes(searchQuery) ||
            Tag.GetSystemTypeText(entry.tagSystemType, t).toLowerCase().includes(searchQuery) ||
            (entry.tagInfo ?? '').toLowerCase().includes(searchQuery),
        )
      : tags;

    return copyAndSort([...base], sortKey, sortDesc);
  }, [tags, searchQuery, sortKey, sortDesc, t]);

  const onColumnClick = (ev?: React.MouseEvent<HTMLElement>, column?: IColumn) => {
    if (!column) return;

    const newColumns: IColumn[] = columns.slice();
    const currColumn: IColumn = newColumns.filter((currCol) => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    setColumns(newColumns);
    setSortKey(currColumn.key);
    setSortDesc(!!currColumn.isSortedDescending);
  };

  const getColumns = (): IColumn[] => [
    {
      key: 'tag',
      name: t('tags:List.Columns.Tag'),
      minWidth: 150,
      maxWidth: 500,
      isMultiline: false,
      isResizable: true,
      onRender: (item?: Tag, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return <KeyValueTag tag={item} />;
      },
    },
    {
      key: 'info',
      name: t('tags:List.Columns.Info'),
      minWidth: 200,
      maxWidth: 400,
      isMultiline: true,
      isResizable: true,
      onRender: (item?: Tag, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return <IPText>{item.tagInfo}</IPText>;
      },
    },
    {
      key: 'systemtype',
      name: t('tags:List.Columns.SystemType'),
      minWidth: 200,
      maxWidth: 500,
      isResizable: true,
      onRender: (item?: Tag, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return (
          <TextInfo message={Tag.GetSystemTypeTextInfo(item.tagSystemType, t)}>
            <IPText>{Tag.GetSystemTypeText(item.tagSystemType, t)}</IPText>
          </TextInfo>
        );
      },
    },
    {
      key: 'used',
      name: t('tags:List.Columns.Used'),
      minWidth: 200,
      maxWidth: 200,
      onRender: (item?: Tag, index?: number | undefined, column?: IColumn | undefined) => {
        if (!item) return;

        return (
          <IPText>
            <IPLink onClick={() => setTagToShowUsage(item)}>{item.usedCount}</IPLink>
          </IPText>
        );
      },
    },
  ];

  const [columns, setColumns] = useState<IColumn[]>(() => getColumns());

  const selection = new IPSelection({
    onSelectionChanged: () => {
      const item = selection.getSelection()[0] as Tag;
      setSelectedTag(item);
    },
  });

  const onAddNewTag = async (item: Tag) => {
    if (isBusy) return;

    try {
      setIsBusy(true);
      appContext.showContentLoader();

      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      const newTag = await apiCreateTag(item, accessToken);

      appContext.globalDataCache.tags.add(newTag);
      setTags([...tags, newTag]);
    } catch (err) {
      appContext.setError(err);
    } finally {
      appContext.hideContentLoader();
      setIsBusy(false);
    }
  };

  const onRemoveTag = async (item: Tag) => {
    if (isBusy) return;

    try {
      setIsBusy(true);
      appContext.showContentLoader();

      const accessToken: string = await appContext.getAccessToken(apiRequest.scopes);
      await apiDeleteTag(item, accessToken);

      appContext.globalDataCache.tags.remove(item);
      setTags(tags.filter((_item) => _item.tagId !== item.tagId));
    } catch (err) {
      if (AppError.isControllerException(err)) {
        if (
          AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_Tag_MustHaveTagsForEachSystemType
        ) {
          appContext.showNotification(
            t('exceptions:Tag.MustHaveTagsForEachSystemTypeDelete', {
              category: Tag.GetSystemTypeText(item.tagSystemType, t),
            }),
            true,
            false,
            false,
          );
        } else {
          appContext.showNotification(AppError.getControllerExceptionMsg(err, t));
        }
      } else {
        appContext.setError(err);
      }
    } finally {
      appContext.hideContentLoader();
      setIsBusy(false);
    }
  };

  const onUpdateTag = async (item: Tag) => {
    if (isBusy) throw new Error('Tag update already in progress');

    try {
      setIsBusy(true);
      appContext.showContentLoader();

      const accessToken = await appContext.getAccessToken(apiRequest.scopes);
      const newTag = await apiUpdateTag(item, accessToken);
      newTag.usedCount = item.usedCount;

      appContext.globalDataCache.tags.update(newTag);
      setTags(tags.map((_item) => (item.tagId === _item.tagId ? newTag : _item)));
    } catch (err) {
      if (AppError.isControllerException(err)) {
        if (
          AppError.getControllerExceptionCode(err) === ControllerExceptionCodes.Code_Tag_MustHaveTagsForEachSystemType
        ) {
          appContext.showNotification(t('exceptions:Tag.MustHaveTagsForEachSystemType'), true, false, true);
        } else {
          appContext.showNotification(AppError.getControllerExceptionMsg(err, t));
        }
      } else {
        appContext.setError(err);
      }
      throw err;
    } finally {
      appContext.hideContentLoader();
      setIsBusy(false);
    }
  };

  const onChangeText = (event?: React.ChangeEvent<HTMLInputElement>, filterText?: string): void => {
    if (filterText) {
      setSearchQuery(filterText.toLowerCase());
    } else {
      setSearchQuery('');
    }
  };

  const targetId = 'uniqueTargetId' + Math.random().toString(36).substring(7);

  const onRenderRow: IDetailsListProps['onRenderRow'] = (props) => {
    if (props) {
      return (
        <span
          onDoubleClick={() => {
            setSelectedTag(props.item);
            setEditTag(true);
          }}
        >
          <DetailsRow {...props} />
        </span>
      );
    }

    return null;
  };

  //
  // Main render
  //
  return (
    <IPStack verticalFill>
      <IPStack verticalFill styles={globalStackStylesPaddingScene} tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <AddEditRemoveButtons
            noLeftPadding
            onClickNew={() => setAddTag(true)}
            onClickEdit={() => (selectedTag ? setEditTag(true) : undefined)}
            onClickRemove={() => (selectedTag ? setRemoveTag(true) : undefined)}
            newDisabled={isLoading || isBusy}
            editDisabled={selectedTag === undefined || isLoading || isBusy}
            removeDisabled={selectedTag === undefined || isLoading || isBusy}
            id={targetId}
          />
        </IPStack.Item>
        <IPStack.Item>
          <IPSearchBox
            underlined
            autoFocus={!appContext.isMobileView}
            placeholder={t('translation:General.Filter.Placeholder')}
            id="search"
            onChange={(ev, newValue) => {
              if (searchTimer.current) {
                clearTimeout(searchTimer.current);
              }
              searchTimer.current = setTimeout(() => onChangeText(ev, newValue), globalFilterDelay);
            }}
            styles={{ root: { width: 300 } }}
          />
        </IPStack.Item>
        <ScrollableStackItem>
          <IPShimmeredDetailsList
            compact
            onRenderRow={onRenderRow}
            onRenderDetailsHeader={onRenderDetailsHeaderGlobal}
            enableShimmer={isLoading}
            items={tagsDisplay}
            selectionMode={IPSelectionMode.single}
            layoutMode={DetailsListLayoutMode.justified}
            constrainMode={ConstrainMode.unconstrained}
            selection={selection}
            shimmerLines={5}
            columns={columns}
            onColumnHeaderClick={onColumnClick}
          />
        </ScrollableStackItem>
        {!isLoading && tagsDisplay && tagsDisplay.length === 0 && (
          <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
            <IPText>{t('translation:General.Notifications.NoItemsFilter')}</IPText>
          </IPStack>
        )}
      </IPStack>
      {addTag && (
        <TagFormPanel isOpen={addTag} onClose={() => setAddTag(false)} title={t('tags:CreatorModal.Title')}>
          {(handlers) => (
            <TagCreator
              tagCache={tags}
              onClose={() => setAddTag(false)}
              onCreate={onAddNewTag}
              saveButtonText={t('translation:General.Button.Save')}
              showCatagory={true}
              hideButtons={true}
              onSaveCallback={handlers.onSaveCallback}
              onSaveDisabledCallback={handlers.onSaveDisabledCallback}
            />
          )}
        </TagFormPanel>
      )}
      {selectedTag && removeTag && (
        <DialogConfirmDelete
          title={t('tags:Remove.Title')}
          subText={t('tags:Remove.SubText')}
          hidden={!removeTag}
          onNo={() => {
            setRemoveTag(false);
          }}
          onYes={() => {
            onRemoveTag(selectedTag);
            setRemoveTag(false);
          }}
        />
      )}
      <TagUsageModal tag={tagToShowUsage} dismiss={() => setTagToShowUsage(undefined)} />
      {editTag && selectedTag && (
        <TagFormPanel isOpen={editTag} onClose={() => setEditTag(false)} title={t('tags:EditorModal.Title')}>
          {(handlers) => (
            <TagEditor
              key={selectedTag.tagId}
              tag={selectedTag}
              tags={tags}
              onClose={() => setEditTag(false)}
              onUpdate={onUpdateTag}
              onSaveCallback={handlers.onSaveCallback}
              onSaveDisabledCallback={handlers.onSaveDisabledCallback}
            />
          )}
        </TagFormPanel>
      )}
    </IPStack>
  );
};

export default TabTags;
